import { memo } from 'react'
import debugWrapper from '../../utils/debugWrapper'
import { useNavigate } from 'react-router-dom'
import ImmersiveAuthPanel from '../ImmersiveAuthPanel'
import CompactChannelsView from '../CompactChannelsView'
import VendorTab from '../checkin/VendorTab'
import AnnouncementsPanel from '../AnnouncementsPanel'
import FAQPanel from '../FAQPanel'
import MapSearchPanel from './MapSearchPanel'
import EntityDisplayPanel from './EntityDisplayPanel'
import LiveFeedPanel from './LiveFeedPanel'

/**
 * ImmersiveTabContent - Renders tab-specific content for immersive map panel
 * Delegates to specialized panels based on active tab
 */
const ImmersiveTabContent = memo(function ImmersiveTabContent({
  // Tab control
  activeTab,
  onTabChange,
  
  // Search tab - Entity mode
  isEntityMode,
  entityDetails,
  latestSighting,
  entityGallery,
  galleryRenderKey,
  entityId,
  panelState,
  onPanelStateChange,
  
  // Entity interactions
  user,
  toggleFavorite,
  isFavorite,
  isTogglingFavorite,
  entityNotifyOnSpot = false,
  onEntityNotifyToggle = null,
  getCategoryIcon,
  getCategoryColor,
  getCategoryIconColor,
  canEditEntity,
  onNavigateToEdit,
  onConfirmLocationClick,
  
  // Gallery state
  selectedImage,
  selectedImageIndex,
  onImageClick,
  onCloseImage,
  onPreviousImage,
  onNextImage,
  
  // Search tab - Search mode
  searchLocation,
  onSearchLocationChange,
  radius,
  onRadiusChange,
  onGeocode,
  onCurrentLocation,
  onSpotClick,
  isGeocoding,
  locationSource,
  entitySearchQuery,
  onEntitySearchChange,
  entitySearchInputRef,
  isSearching,
  searchResults,
  showDropdown,
  onSelectEntity,
  onCloseDropdown,
  userLocation,
  searchLocationName,
  entityCount,
  isLoading,
  onShare,
  
  // Inline filter chips (for search tab)
  activeFilters = [],
  onFilterToggle = null,
  openNowActive = false,
  onToggleOpenNow = null,
  showEvents = true,
  onToggleShowEvents = null,

  // Auth tabs
  stats,
  statsLoading,
  pendingConfirmations,
  loadingConfirmations,
  confirmingId,
  handleConfirmSighting,
  loginFormData,
  loginErrors,
  handleLoginChange,
  handleLoginSubmit,
  registerFormData,
  registerErrors,
  handleRegisterChange,
  handleRegisterSubmit,
  isAuthLoading,
  googleLoginUrl,
  
  // Contact tab
  initialChannelId,
  onChannelSelected,
  onUnreadCountChange,
  claimPrefill,
  onClaimPrefillConsumed,
  
  // Search sort
  searchSortBy = 'recent',
  onSearchSortByChange = null,
}) {
  const navigate = useNavigate()
  
  // Search Tab Content
  if (activeTab === 'search') {
    return (
      <>
        {isEntityMode && entityDetails ? (
          <EntityDisplayPanel
            entityDetails={entityDetails}
            latestSighting={latestSighting}
            entityGallery={entityGallery}
            entityId={entityId}
            user={user}
            isFavorite={isFavorite}
            isTogglingFavorite={isTogglingFavorite}
            toggleFavorite={toggleFavorite}
            entityNotifyOnSpot={entityNotifyOnSpot}
            onEntityNotifyToggle={onEntityNotifyToggle}
            panelState={panelState}
            onPanelStateChange={onPanelStateChange}
            onTabChange={onTabChange}
            selectedImage={selectedImage}
            selectedImageIndex={selectedImageIndex}
            onImageClick={onImageClick}
            onCloseImage={onCloseImage}
            onPreviousImage={onPreviousImage}
            onNextImage={onNextImage}
            galleryRenderKey={galleryRenderKey}
            getCategoryIcon={getCategoryIcon}
            getCategoryColor={getCategoryColor}
            getCategoryIconColor={getCategoryIconColor}
            userLocation={userLocation}
            canEditEntity={canEditEntity}
            onNavigateToEdit={onNavigateToEdit}
            onConfirmLocationClick={onConfirmLocationClick}
          />
        ) : (
          panelState !== 'minimized' && (
            <MapSearchPanel
              searchLocation={searchLocation}
              onSearchLocationChange={onSearchLocationChange}
              radius={radius}
              onRadiusChange={onRadiusChange}
              onGeocode={onGeocode}
              onCurrentLocation={onCurrentLocation}
              onSpotClick={onSpotClick}
              isGeocoding={isGeocoding}
              locationSource={locationSource}
              entitySearchQuery={entitySearchQuery}
              onEntitySearchChange={onEntitySearchChange}
              entitySearchInputRef={entitySearchInputRef}
              isSearching={isSearching}
              searchResults={searchResults}
              showDropdown={showDropdown}
              onSelectEntity={onSelectEntity}
              onCloseDropdown={onCloseDropdown}
              userLocation={userLocation}
              searchLocationName={searchLocationName}
              entityCount={entityCount}
              isLoading={isLoading}
              onShare={onShare}
              size="compact"
              sortBy={searchSortBy}
              onSortByChange={onSearchSortByChange}
              activeFilters={activeFilters}
              onFilterToggle={onFilterToggle}
              openNowActive={openNowActive}
              onToggleOpenNow={onToggleOpenNow}
              showEvents={showEvents}
              onToggleShowEvents={onToggleShowEvents}
            />
          )
        )}
      </>
    )
  }
  
  // Auth Tab
  if (activeTab === 'auth') {
    return (
      <ImmersiveAuthPanel
        user={user}
        stats={stats}
          statsLoading={statsLoading}
          pendingConfirmations={pendingConfirmations}
          loadingConfirmations={loadingConfirmations}
          confirmingId={confirmingId}
          handleConfirmSighting={handleConfirmSighting}
          userLocation={userLocation}
          entityCount={entityCount}
          loginFormData={loginFormData}
          loginErrors={loginErrors}
          handleLoginChange={handleLoginChange}
          handleLoginSubmit={handleLoginSubmit}
          registerFormData={registerFormData}
          registerErrors={registerErrors}
          handleRegisterChange={handleRegisterChange}
          handleRegisterSubmit={handleRegisterSubmit}
          isAuthLoading={isAuthLoading}
          googleAuth={googleLoginUrl}
          onTabChange={onTabChange}
          navigate={navigate}
        />
    )
  }
  
  // Contact Tab
  if (activeTab === 'contact') {
    return (
      <CompactChannelsView 
        user={user} 
        initialChannelId={initialChannelId} 
        onChannelSelected={onChannelSelected}
        onUnreadCountChange={onUnreadCountChange}
        onTabChange={onTabChange}
        claimPrefill={claimPrefill}
        onClaimPrefillConsumed={onClaimPrefillConsumed}
      />
    )
  }
  
  // Announcements Tab
  if (activeTab === 'announcements') {
    return <AnnouncementsPanel />
  }
  
  // FAQ Tab
  if (activeTab === 'faq') {
    return <FAQPanel />
  }

  // Vendor Tab
  if (activeTab === 'vendor') {
    return (
      <VendorTab
        onOpenContact={(opts) => onTabChange?.('contact', null, opts?.channelId)}
        onOpenChannel={(channelId) => onTabChange?.('contact', null, channelId)}
      />
    )
  }

  // Live Feed Tab
  if (activeTab === 'live') {
    return <LiveFeedPanel userLocation={userLocation} />
  }
  
  return null
})

export default debugWrapper(ImmersiveTabContent, 'ImmersiveTabContent', 'src/components/map/ImmersiveTabContent.jsx')
